'use client';
import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { elowenClient } from '../elowenClient';
import { QUERY_KEYS } from '../queries';
import type { AccountDefaultsSettingsPatch, AccountDefaultsView, ApplyField } from '../accountDefaults';

/** The revision a defaults write is built on. Read from the cache the window renders, so a save always
 *  carries the revision the reader was looking at; with nothing loaded there is nothing to save from, and
 *  that is a programming error rather than a state to paper over with a zero. */
function loadedRevision(qc: QueryClient): number {
  const view = qc.getQueryData<AccountDefaultsView>(QUERY_KEYS.usersDefaults);
  if (!view) throw new Error('account defaults are not loaded');
  return view.revision;
}

/** ADMIN: write the stored defaults. The answer replaces the cached view, so the own-value counts and the
 *  effective values are the daemon's own rather than a local guess. A plugin's default form is read from
 *  the same revision, so its listing is refreshed too. */
export function useSaveUsersDefaults() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: { settings?: AccountDefaultsSettingsPatch; newAccountPresetId?: number | null }) =>
      elowenClient.saveUsersDefaults(patch, loadedRevision(qc)),
    onSuccess: (saved) => { qc.setQueryData(QUERY_KEYS.usersDefaults, saved); void qc.invalidateQueries({ queryKey: QUERY_KEYS.usersDefaultsPlugins }); void qc.invalidateQueries({ queryKey: QUERY_KEYS.accountPresets }); },
  });
}

/** ADMIN: clear the named fields' own values on every human account. Both listings are refetched: an
 *  apply moves the counts the window renders and every account's effective values. */
export function useApplyUsersDefaults() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (fields: ApplyField[]) => elowenClient.applyUsersDefaults(fields),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: QUERY_KEYS.usersDefaults });
      void qc.invalidateQueries({ queryKey: QUERY_KEYS.usersDefaultsPlugins });
    },
  });
}
